import { html, TemplateResult, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { hasContent } from '@common/lit/hasContent.js';
import { Generation } from '@api-consent/schema/Enums.js';
import type { TefComs4csMicroFrontend } from '@src/components/tef-coms4cs.js';
import { replaceElementsBound } from '@src/components/templates/util.js';
import { comsStore } from '@src/state/ComsStore.js';
import type { ConsentGroupStore } from '@src/state/ConsentGroupStore.js';

function getGroupText(this: TefComs4csMicroFrontend, textGroup: ConsentGroupStore): TemplateResult {
  return textGroup.text
    ? html` ${unsafeHTML(replaceElementsBound(this, textGroup.text))} `
    : nothing as unknown as TemplateResult;
}

// start oneclick checkbox, text and container
function getOneclickHeader(this: TefComs4csMicroFrontend, oGroup: ConsentGroupStore): TemplateResult {
  let labelText = oGroup.text;
  if (oGroup.oneclick && hasContent(oGroup.oneclickScope)) {
    labelText = oGroup.oneclickScope;
  }

  return html`
    <one-cluster gap="sm" no-wrap x="start" y="start">
      <one-checkbox
        .checked=${oGroup.areAllConsentsAndFeaturesGranted}
        id="group-${oGroup.id}"
        type="checkbox"
        @click=${(e: PointerEvent) => { oGroup.oneClickToggle(e); }}
        ?disabled="${comsStore.identity?.readonly ?? false}"
        >
      </one-checkbox>
      <label for="group-${oGroup.id}">
        ${unsafeHTML(replaceElementsBound(this, labelText))}
      </label>
    </one-cluster>
  `;
}

function getGroupOneclickConsentsContainer(this: TefComs4csMicroFrontend, containerGroup: ConsentGroupStore)
  : TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  const defaultText = 'Wenn ich einzelne Kontaktkanäle und Verarbeitungsvorgänge von meiner Auswahl ausschließen möchte, kann ich hier gesonderte Einwilligungen erteilen.';

  return html`
    <one-accordion
      ?is-open="${containerGroup.collapsibleOpen}"
      cloak
      context=${comsStore.config?.styleContext ?? ''}
      id=${containerGroup.name}
    >
      <one-accordion-item once>
        <div slot="summary">
          ${containerGroup.oneclickCollapsible ? unsafeHTML(containerGroup.oneclickCollapsible) : defaultText}
        </div>
        <one-stack gap="md">
          <!-- consentPrefix -->
          ${containerGroup.consentPrefix ? html`<one-text>${unsafeHTML(replaceElementsBound(this, containerGroup.consentPrefix))}</one-text>` : nothing}
          ${containerGroup.consents.map((consent) => consentTemplate(consent, containerGroup))}
        </one-stack>
      </one-accordion-item>
    </one-accordion>
  `;
}
// end oneclick text and container

// denyAll - OCW checkbox
function getWithdrawal(ocwGroup: ConsentGroupStore): TemplateResult {
  if (!ocwGroup.denyall || ocwGroup.oneclick) {
    return nothing as unknown as TemplateResult;
  }

  const checkboxId = `deny_check_${ocwGroup.id}`;

  return html`
    <one-cluster gap="sm" no-wrap>
      <one-checkbox
        id=${checkboxId}
        type="checkbox"
        .checked=${ocwGroup.areAllConsentsDenied}
        @click=${(e: PointerEvent) => ocwGroup.denyAll(e)}>
      </one-checkbox>
      <label for=${checkboxId}>
        ${unsafeHTML(ocwGroup.denyall)}
      </label>
    </one-cluster>
  `;
}

export function groupTemplate(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const consentTemplate = this.getTemplateFn('consentTemplate');

  let consentHtml: TemplateResult[] = [];
  if (group.oneclick) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-call
    consentHtml.push(getGroupOneclickConsentsContainer.bind(this)(group));
  } else {
    if (group.consentPrefix) {
      consentHtml.push(html`<one-text>${unsafeHTML(replaceElementsBound(this, group.consentPrefix))}</one-text>`);
    }
    consentHtml = consentHtml.concat(group.consents.map((consent) => consentTemplate(consent, group)));
  }

  return html`
  <one-content data-testid="group-${group.name?.toLowerCase()}">
    <one-stack gap="md">
      <one-heading level="h4">
      ${unsafeHTML(replaceElementsBound(this, group.header))}
      </one-heading>
      ${group.oneclick && this.store.generation === Generation.UNLOCK ? html`<one-text as="p">${unsafeHTML(replaceElementsBound(this, group.text))}</one-text>` : nothing}
      <one-text as="p">
         ${/* eslint-disable-line */ group.oneclick ? getOneclickHeader.bind(this)(group) : getGroupText.bind(this)(group)}
      </one-text>
      <one-stack gap="sm">
        ${consentHtml}
      </one-stack>
      ${group.footer ? html`
        <one-text as="p">
          ${unsafeHTML(replaceElementsBound(this, group.footer))}
        </one-text>
      ` : nothing}
      ${getWithdrawal(group)}
      </div>
      <one-divider variant="default"></one-divider>
    </one-stack>
  </one-content>
`;
}

export function groupTemplateOverview(this: TefComs4csMicroFrontend, group: ConsentGroupStore): TemplateResult {
  const filteredConsents = group.consents
    .filter(({ granted = false }) => granted === true);

  const consentTemplateOverview = this.getTemplateFn('consentTemplateOverview');

  return html`
    <one-stack gap="xxs" data-testid="group-${group.name?.toLowerCase()}">
      <one-heading level="h4">
        ${unsafeHTML(replaceElementsBound(this, group.header))}
      </one-heading>
      <div>
        ${(filteredConsents.length === 0) /* eslint-disable @typescript-eslint/indent */
          ? html`
            <one-paper background="legal-2" padding="sm" text-color="make-links-blau">
              ${group.denyallView ?? 'es liegen keine Erlaubnisse / Einwilligungen vor'}
            </one-paper>`
          : html`
            <one-text as="div">
              ${group.consents.map((consent) => (
                consent.granted ? consentTemplateOverview(consent) : nothing))}
            </one-text>`
        /* eslint-enable @typescript-eslint/indent */}
      </div>
    </one-stack>
    <one-divider variant="default"></one-divider>
  `;
}
